import {
  measurePerformance,
  type PerformanceCacheStatus,
  type PerformanceThread,
} from "../perf/index.js";

export type RoqeetSurface = "ko" | "en" | "mixed";

export interface RoqeetDocument {
  kind: "roqeet.ast";
  languageVersion: string;
  nodes: RoqeetNode[];
  errors: RoqeetParseError[];
}

export type RoqeetNode =
  | RoqeetTextNode
  | RoqeetSetNode
  | RoqeetChangeNode
  | RoqeetPushNode
  | RoqeetUnsetNode
  | RoqeetIfNode
  | RoqeetRandomNode
  | RoqeetPromptNode
  | RoqeetDialogueNode
  | RoqeetButtonNode
  | RoqeetChoicesNode
  | RoqeetImageNode
  | RoqeetSceneNode
  | RoqeetRollNode
  | RoqeetCommentNode
  | RoqeetEnvNode
  | RoqeetGameMetaNode
  | RoqeetStateSchemaNode;

export interface RoqeetBaseNode {
  type: string;
  line: number;
  surface: RoqeetSurface;
}

export interface RoqeetTextNode extends RoqeetBaseNode {
  type: "Text";
  text: string;
}

export interface RoqeetSetNode extends RoqeetBaseNode {
  type: "Set";
  target: string;
  expression: string;
}

export interface RoqeetChangeNode extends RoqeetBaseNode {
  type: "Change";
  op: "increase" | "decrease";
  target: string;
  amount: string;
  min?: string;
  max?: string;
}

export interface RoqeetPushNode extends RoqeetBaseNode {
  type: "Push";
  target: string;
  value: string;
}

export interface RoqeetUnsetNode extends RoqeetBaseNode {
  type: "Unset";
  target: string;
}

export interface RoqeetIfNode extends RoqeetBaseNode {
  type: "If";
  condition: string;
  then: RoqeetNode[];
  elseIf?: RoqeetElseIfClause[];
  else?: RoqeetNode[];
  partial?: boolean;
}

export interface RoqeetElseIfClause {
  condition: string;
  then: RoqeetNode[];
}

export interface RoqeetRandomNode extends RoqeetBaseNode {
  type: "Random";
  items: RoqeetNode[];
}

export interface RoqeetPromptNode extends RoqeetBaseNode {
  type: "Prompt";
  name: string;
  body: RoqeetNode[];
}

export interface RoqeetDialogueNode extends RoqeetBaseNode {
  type: "Dialogue";
  speaker: string;
  body: RoqeetNode[];
}

export interface RoqeetButtonNode extends RoqeetBaseNode {
  type: "Button";
  label: string;
  action: string;
}

export interface RoqeetChoiceItem {
  label: string;
  action?: string;
}

export interface RoqeetChoicesNode extends RoqeetBaseNode {
  type: "Choices";
  items: RoqeetChoiceItem[];
}

export interface RoqeetImageNode extends RoqeetBaseNode {
  type: "Image";
  asset: string;
}

export interface RoqeetSceneNode extends RoqeetBaseNode {
  type: "Scene";
  id: string;
  props: Record<string, string>;
  body: RoqeetNode[];
}

export interface RoqeetRollNode extends RoqeetBaseNode {
  type: "Roll";
  notation: string;
}

export interface RoqeetCommentNode extends RoqeetBaseNode {
  type: "Comment";
  text: string;
}

export interface RoqeetEnvNode extends RoqeetBaseNode {
  type: "Env";
  values: Record<string, RoqeetValue>;
}

export interface RoqeetGameMetaNode extends RoqeetBaseNode {
  type: "GameMeta";
  values: Record<string, RoqeetValue>;
}

export type RoqeetStateValueType = "number" | "string" | "boolean" | "enum";
export type RoqeetStateMutableBy = "ai" | "engine" | "readonly";

export interface RoqeetStateDeclaration {
  key: string;
  valueType: RoqeetStateValueType;
  initial: RoqeetValue;
  min?: number;
  max?: number;
  values?: string[];
  mutableBy?: RoqeetStateMutableBy;
  mutable?: boolean;
  derived?: boolean;
  transient?: boolean;
  label?: string;
}

export interface RoqeetStateSchemaNode extends RoqeetBaseNode {
  type: "StateSchema";
  field: RoqeetStateDeclaration;
}

export interface RoqeetParseError {
  line: number;
  code:
    | "UNEXPECTED_CLOSE"
    | "UNCLOSED_BLOCK"
    | "INVALID_COMMAND"
    | "INVALID_ASSIGNMENT"
    | "INVALID_EXPRESSION"
    | "UNKNOWN_IDENTIFIER"
    | "TYPE_MISMATCH";
  message: string;
}

export type RoqeetValue = string | number | boolean | null | RoqeetValue[] | { [key: string]: RoqeetValue };

export interface RoqeetJsonDocument {
  kind: "roqeet.ir";
  languageVersion: string;
  irVersion: string;
  nodes: RoqeetJsonNode[];
}

export type RoqeetJsonNode =
  | { type: "text"; text: string }
  | { type: "set"; target: string; expression: { rex: string } }
  | {
    type: "change";
    op: "increase" | "decrease";
    target: string;
    amount: { rex: string };
    min?: { rex: string };
    max?: { rex: string };
  }
  | { type: "push"; target: string; value: { rex: string } }
  | { type: "unset"; target: string }
  | {
    type: "if";
    condition: { rex: string };
    then: RoqeetJsonNode[];
    elseIf?: { condition: { rex: string }; then: RoqeetJsonNode[] }[];
    else?: RoqeetJsonNode[];
  }
  | { type: "random"; items: RoqeetJsonNode[] }
  | { type: "prompt"; name: string; body: RoqeetJsonNode[] }
  | { type: "dialogue"; speaker: string; body: RoqeetJsonNode[] }
  | { type: "button"; label: string; action: string }
  | { type: "choices"; items: RoqeetChoiceItem[] }
  | { type: "image"; asset: string }
  | { type: "scene"; id: string; props: Record<string, string>; body: RoqeetJsonNode[] }
  | { type: "roll"; notation: string }
  | { type: "comment"; text: string }
  | { type: "env"; values: Record<string, RoqeetValue> }
  | { type: "game"; values: Record<string, RoqeetValue> }
  | { type: "state"; field: RoqeetStateDeclaration };

export interface RoqeetRunContext {
  char?: string;
  user?: string;
  state?: Record<string, unknown>;
  values?: Record<string, unknown>;
  random?: () => number;
  now?: () => number;
  diagnostics?: RoqeetParseError[];
  diagnosticLine?: number;
}

export interface RoqeetPipelineOptions {
  fileId?: string;
  packageId?: string;
  cache?: PerformanceCacheStatus;
  thread?: PerformanceThread;
  invalidationReason?: string;
  detail?: Record<string, string | number | boolean | null | undefined>;
}

export interface RoqeetChangedRange {
  from: number;
  to: number;
  insertedText: string;
}

export interface RoqeetPromptOutput {
  name: string;
  content: string;
}

export interface RoqeetUiOutput {
  kind: "button" | "choice";
  label: string;
  action: string;
}

export interface RoqeetMediaOutput {
  kind: "image";
  asset: string;
}

export interface RoqeetDialogueOutput {
  speaker: string;
  content: string;
}

export interface RoqeetSceneOutput {
  id: string;
  props: Record<string, string>;
}

export interface RoqeetRollOutput {
  notation: string;
  result: number;
}

export interface RoqeetRunResult {
  text: string;
  prompts: RoqeetPromptOutput[];
  ui: RoqeetUiOutput[];
  media: RoqeetMediaOutput[];
  dialogues: RoqeetDialogueOutput[];
  scenes: RoqeetSceneOutput[];
  rolls: RoqeetRollOutput[];
  state: Record<string, unknown>;
  json: RoqeetJsonDocument;
  errors: RoqeetParseError[];
}

let roqeetCompilerWarmed = false;
let roqeetCompilerWarmupSource: string | undefined;

interface SourceLine {
  text: string;
  line: number;
}

interface ParseState {
  lines: SourceLine[];
  errors: RoqeetParseError[];
}

const COMMANDS = {
  set: new Set(["\uC124\uC815", "set"]),
  increase: new Set(["\uC99D\uAC00", "increase"]),
  decrease: new Set(["\uAC10\uC18C", "decrease"]),
  push: new Set(["\uCD94\uAC00", "push", "add"]),
  unset: new Set(["\uC0AD\uC81C", "unset", "delete"]),
  if: new Set(["\uB9CC\uC57D", "if"]),
  else: new Set(["\uC544\uB2C8\uBA74", "else"]),
  random: new Set(["\uB79C\uB364", "random"]),
  prompt: new Set(["\uD504\uB86C\uD504\uD2B8", "prompt"]),
  dialogue: new Set(["\uB300\uC0AC", "dialogue"]),
  button: new Set(["\uBC84\uD2BC", "button"]),
  choices: new Set(["\uC120\uD0DD\uC9C0", "choices", "menu"]),
  image: new Set(["\uC774\uBBF8\uC9C0", "image"]),
  scene: new Set(["\uC7A5\uBA74", "scene"]),
  roll: new Set(["\uC8FC\uC0AC\uC704", "roll", "dice"]),
  comment: new Set(["\uC8FC\uC11D", "comment"]),
  env: new Set(["\uD658\uACBD", "env"]),
  game: new Set(["\uAC8C\uC784", "game"]),
  state: new Set(["\uC0C1\uD0DC", "state"]),
};

const COMMAND_GROUPS = Object.values(COMMANDS);
const KNOWN_COMMAND_NAMES = new Set(COMMAND_GROUPS.flatMap((commands) => [...commands]));
const INLINE_LITERAL_COMMAND_NAMES = new Set([...KNOWN_COMMAND_NAMES, "inc", "dec"]);
type UnsupportedCommandStatus = "Preview" | "Experimental" | "Removed";

const UNSUPPORTED_DOCUMENTED_COMMANDS = new Map<string, { status: UnsupportedCommandStatus; guidance: string }>([
  ...unsupportedCommands(
    ["\uC751\uB2F5\uC804", "before_response", "\uC751\uB2F5\uD6C4", "after_response"],
    "Preview",
    "Response hooks are documented as Preview and are not executable in this parser yet. Move model guidance into @prompt, or keep hook code in .rqg/JSON until hook execution is implemented.",
  ),
  ...unsupportedCommands(
    [
      "\uAE30\uC5B5\uC800\uC7A5",
      "memory_set",
      "\uAE30\uC5B5\uC77D\uAE30",
      "memory_get",
      "\uAE30\uC5B5\uC0AD\uC81C",
      "memory_delete",
      "\uAE30\uC5B5\uAC80\uC0C9",
      "memory_search",
    ],
    "Preview",
    "Memory commands are documented as Preview and are not executable in this parser yet. Use explicit state commands for game state, or wait for the memory contract.",
  ),
  ...unsupportedCommands(
    ["\uD2B8\uB9AC\uAC70", "trigger", "\uBAA8\uB378\uD504\uB85C\uD544", "model_profile", "\uBAA8\uB378\uD638\uCD9C", "model_call"],
    "Preview",
    "This command is documented as Preview and is not executable in this parser yet. Stable .rqt currently supports state, branching, prompts, UI, scenes, images, and dice.",
  ),
  ...unsupportedCommands(
    ["\uCE90\uB9AD\uD130", "character", "\uC2A4\uD0EF", "stat"],
    "Preview",
    "Character declarations are documented as Preview and are not executable in this parser yet. Use @dialogue/@prompt for current Stable authoring.",
  ),
  ...unsupportedCommands(
    [
      "\uBC30\uACBD",
      "background",
      "\uC74C\uC545",
      "music",
      "\uC74C\uC545\uC815\uC9C0",
      "stop_music",
      "\uC2A4\uD504\uB77C\uC774\uD2B8",
      "sprite",
      "\uC2A4\uD504\uB77C\uC774\uD2B8\uD45C\uC2DC",
      "show_sprite",
      "\uC2A4\uD504\uB77C\uC774\uD2B8\uC815\uC9C0",
      "stop_sprite",
      "\uD45C",
      "table",
    ],
    "Experimental",
    "This media/table command is documented as Experimental and is not executable in this parser yet. Use @image or @scene metadata for current Stable authoring.",
  ),
  ...unsupportedCommands(
    ["\uD568\uC218", "function", "\uBD88\uB7EC\uC624\uAE30", "import", "\uD14C\uC2A4\uD2B8", "test", "\uB85C\uADF8", "log", "\uC7A5\uBA74\uC774\uB3D9", "goto_scene"],
    "Experimental",
    "This tool command is documented as Experimental and is not executable in this parser yet. Keep current guide fixtures to Stable commands.",
  ),
  ...unsupportedCommands(
    ["\uB05D", "end"],
    "Removed",
    "The @end/@\uB05D block closer was removed from .rqt 0.2. Use { } block braces instead.",
  ),
]);
const CRLF_RE = /\r\n?/g;
const HEADER_RE = /^#!roqeet\/rqt\s+(.+)$/;
const LEADING_BULLET_RE = /^-\s+/;
const HANGUL_SCRIPT_RE = /\p{Script=Hangul}/u;
const ALPHA_RE = /^[A-Za-z]+$/;
const ASCII_WORD_CHAR_RE = /^[A-Za-z0-9_]$/;
const INLINE_IDENTIFIER_RE = /^@([\p{L}_][\p{L}\p{N}_]*)/u;
const WHITESPACE_RE = /\s/;
const NUMBER_PREFIX_RE = /^\d+(?:\.\d+)?/;
const REX_IDENTIFIER_PREFIX_RE = /^[\p{L}_][\p{L}\p{N}_.-]*/u;
const UNSAFE_OBJECT_KEYS = new Set(["__proto__", "constructor", "prototype"]);
type GameMetaAttr = "title" | "description" | "icon";
type StateAttr = "initial" | "min" | "max" | "values" | "mutableBy" | "mutable" | "derived" | "transient" | "label";
type SceneAttr = "title";

const GAME_META_ATTRS: ReadonlyMap<string, GameMetaAttr> = new Map([
  ["title", "title"],
  ["name", "title"],
  ["\uC81C\uBAA9", "title"],
  ["\uC774\uB984", "title"],
  ["description", "description"],
  ["summary", "description"],
  ["\uC124\uBA85", "description"],
  ["\uC694\uC57D", "description"],
  ["icon", "icon"],
  ["\uC544\uC774\uCF58", "icon"],
] as const);
const STATE_ATTRS: ReadonlyMap<string, StateAttr> = new Map([
  ["initial", "initial"],
  ["default", "initial"],
  ["\uCD08\uAE30", "initial"],
  ["\uAE30\uBCF8\uAC12", "initial"],
  ["min", "min"],
  ["\uCD5C\uC18C", "min"],
  ["max", "max"],
  ["\uCD5C\uB300", "max"],
  ["values", "values"],
  ["\uAC12\uB4E4", "values"],
  ["mutableBy", "mutableBy"],
  ["\uBCC0\uACBD\uC8FC\uCCB4", "mutableBy"],
  ["mutable", "mutable"],
  ["\uBCC0\uACBD\uAC00\uB2A5", "mutable"],
  ["derived", "derived"],
  ["\uD30C\uC0DD", "derived"],
  ["transient", "transient"],
  ["\uC784\uC2DC", "transient"],
  ["label", "label"],
  ["\uB77C\uBCA8", "label"],
] as const);
const SCENE_ATTRS: ReadonlyMap<string, SceneAttr> = new Map([
  ["title", "title"],
  ["name", "title"],
  ["\uC81C\uBAA9", "title"],
  ["\uC774\uB984", "title"],
] as const);
const STATE_MUTABLE_BY = new Set<RoqeetStateMutableBy>(["ai", "engine", "readonly"]);

function unsupportedCommands(
  names: string[],
  status: UnsupportedCommandStatus,
  guidance: string,
): Array<[string, { status: UnsupportedCommandStatus; guidance: string }]> {
  return names.map((name) => [name, { status, guidance }]);
}

export function parseRoqeet(source: string, options: RoqeetPipelineOptions = {}): RoqeetDocument {
  return measurePerformance(
    "parse_rqt_file",
    roqeetSpanMeta(source.length, options, "full_parse"),
    () => parseRoqeetInternal(source),
  );
}

export function parseRoqeetChangedRange(
  source: string,
  range: RoqeetChangedRange,
  options: RoqeetPipelineOptions = {},
): RoqeetDocument {
  return measurePerformance(
    "parse_changed_range",
    roqeetSpanMeta(source.length, options, "fallback_full_reparse", {
      changedFrom: range.from,
      changedTo: range.to,
      insertedSize: range.insertedText.length,
    }),
    () => parseRoqeetInternal(source),
  );
}

function parseRoqeetInternal(source: string): RoqeetDocument {
  const withoutBom = source.charCodeAt(0) === 0xfeff ? source.slice(1) : source;
  const normalized = withoutBom.includes("\r") ? withoutBom.replace(CRLF_RE, "\n") : withoutBom;
  const rawLines = normalized.split("\n");
  let languageVersion = "0.1";
  const lines: SourceLine[] = [];

  for (let i = 0; i < rawLines.length; i++) {
    const text = rawLines[i] ?? "";
    const header = text.startsWith("#!roqeet/rqt") ? HEADER_RE.exec(text.trim()) : null;
    if (header) {
      languageVersion = header[1]?.trim() || languageVersion;
      continue;
    }
    lines.push({ text, line: i + 1 });
  }

  const state: ParseState = { lines, errors: [] };
  const [nodes] = parseBlock(state, 0, false);
  return { kind: "roqeet.ast", languageVersion, nodes, errors: state.errors };
}

export function semanticCheckRoqeet(
  document: RoqeetDocument,
  options: RoqeetPipelineOptions = {},
): RoqeetParseError[] {
  return measurePerformance(
    "semantic_check",
    roqeetSpanMeta(document.nodes.length, options, "parse_errors_only"),
    () => document.errors,
  );
}

export function resolveRoqeetImportGraph(
  document: RoqeetDocument,
  options: RoqeetPipelineOptions = {},
): string[] {
  return measurePerformance(
    "resolve_import_graph",
    roqeetSpanMeta(document.nodes.length, options, "no_imports_declared"),
    () => [],
  );
}

export function compileRoqeetToJson(
  document: RoqeetDocument,
  options: RoqeetPipelineOptions = {},
): RoqeetJsonDocument {
  return measurePerformance(
    "lower_to_ir",
    roqeetSpanMeta(document.nodes.length, options, "ast_to_json"),
    () => ({
      kind: "roqeet.ir",
      languageVersion: document.languageVersion,
      irVersion: document.languageVersion.startsWith("0.2") ? "0.2" : "0.1",
      nodes: document.nodes.map(toJsonNode),
    }),
  );
}

export function optimizeRoqeetIr(
  json: RoqeetJsonDocument,
  options: RoqeetPipelineOptions = {},
): RoqeetJsonDocument {
  return measurePerformance(
    "optimize_ir",
    roqeetSpanMeta(json.nodes.length, options, "no_optimizer_configured"),
    () => json,
  );
}

export function runRoqeet(
  source: string,
  context: RoqeetRunContext = {},
  options: RoqeetPipelineOptions = {},
): RoqeetRunResult {
  const document = parseRoqeet(source, options);
  return runRoqeetDocument(source, document, context, options);
}

export function runRoqeetChangedRange(
  source: string,
  range: RoqeetChangedRange,
  context: RoqeetRunContext = {},
  options: RoqeetPipelineOptions = {},
): RoqeetRunResult {
  const document = parseRoqeetChangedRange(source, range, options);
  return runRoqeetDocument(source, document, context, options);
}

function runRoqeetDocument(
  source: string,
  document: RoqeetDocument,
  context: RoqeetRunContext,
  options: RoqeetPipelineOptions,
): RoqeetRunResult {
  semanticCheckRoqeet(document, options);
  resolveRoqeetImportGraph(document, options);
  const json = optimizeRoqeetIr(compileRoqeetToJson(document, options), options);
  const runtime: RuntimeState = {
    context,
    errors: [...document.errors],
    state: { ...(context.state ?? {}) },
    text: [],
    prompts: [],
    ui: [],
    media: [],
    dialogues: [],
    scenes: [],
    rolls: [],
  };
  measurePerformance(
    "render_preview",
    roqeetSpanMeta(source.length, options, "runtime_preview"),
    () => executeNodes(document.nodes, runtime, "text"),
  );
  return {
    text: runtime.text.join("\n"),
    prompts: runtime.prompts,
    ui: runtime.ui,
    media: runtime.media,
    dialogues: runtime.dialogues,
    scenes: runtime.scenes,
    rolls: runtime.rolls,
    state: runtime.state,
    json,
    errors: runtime.errors,
  };
}

export function runRoqeetTestCase(
  source: string,
  context: RoqeetRunContext = {},
  options: RoqeetPipelineOptions = {},
): RoqeetRunResult {
  return measurePerformance(
    "run_test_case",
    roqeetSpanMeta(source.length, options, "test_case"),
    () => runRoqeet(source, context, options),
  );
}

export function fullBuildRoqeet(
  source: string,
  context: RoqeetRunContext = {},
  options: RoqeetPipelineOptions = {},
): RoqeetRunResult {
  return measurePerformance(
    "full_build",
    roqeetSpanMeta(source.length, options, "full_build"),
    () => runRoqeet(source, context, options),
  );
}

export function warmRoqeetCompiler(options: RoqeetPipelineOptions = {}): void {
  if (roqeetCompilerWarmed) return;
  roqeetCompilerWarmed = true;
  runRoqeet(
    getRoqeetCompilerWarmupSource(),
    { char: "Roqeet", user: "Builder", values: { warmup: 1 } },
    {
      ...options,
      fileId: options.fileId ?? "compiler-warmup.rqt",
      invalidationReason: options.invalidationReason ?? "compiler_warmup",
      detail: { ...(options.detail ?? {}), warmup: true },
    },
  );
}

function getRoqeetCompilerWarmupSource(): string {
  if (roqeetCompilerWarmupSource) return roqeetCompilerWarmupSource;
  const lines = ["#!roqeet/rqt 0.1"];
  let size = lines[0]!.length;
  const push = (line: string) => {
    lines.push(line);
    size += line.length + 1;
  };
  for (let i = 0; size < 12 * 1024; i++) {
    push(`@set warmup = ${i % 100}`);
    push(`Warmup ${i}: @user meets @char with score @(warmup + ${i % 7}).`);
    push("@if warmup >= 50 {");
    push("  Warm compiler preview branch.");
    push("}");
  }
  roqeetCompilerWarmupSource = lines.join("\n");
  return roqeetCompilerWarmupSource;
}

function roqeetSpanMeta(
  inputSize: number,
  options: RoqeetPipelineOptions,
  defaultInvalidationReason: string,
  detail?: Record<string, string | number | boolean | null | undefined>,
) {
  return {
    inputSize,
    fileId: options.fileId ?? "inline.rqt",
    packageId: options.packageId ?? "",
    cache: options.cache ?? "bypass",
    thread: options.thread ?? "main",
    invalidationReason: options.invalidationReason ?? defaultInvalidationReason,
    detail: { ...(options.detail ?? {}), ...(detail ?? {}) },
  };
}

type RoqeetExpressionErrorCode = "INVALID_EXPRESSION" | "UNKNOWN_IDENTIFIER" | "TYPE_MISMATCH";

class RoqeetExpressionError extends Error {
  constructor(
    readonly code: RoqeetExpressionErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "RoqeetExpressionError";
  }
}

export function renderRoqeetText(input: string, context: RoqeetRunContext = {}): string {
  let out = "";
  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (ch !== "@") {
      out += ch;
      continue;
    }

    const next = input[i + 1];
    if (next === "@") {
      out += "@";
      i++;
      continue;
    }
    if (next === "(") {
      const found = readInlineExpression(input, i + 2);
      if (!found) {
        pushRoqeetDiagnostic(context, "INVALID_EXPRESSION", "Malformed inline expression: @(...) is missing a closing ')'.");
        out += ch;
        continue;
      }
      try {
        const value = evaluateRoqeetExpression(found.expression, context);
        out += formatValue(value);
      } catch (error) {
        pushRoqeetExpressionDiagnostic(context, error, `@(${found.expression})`);
        out += `@(${found.expression})`;
      }
      i = found.end;
      continue;
    }

    const reserved = readReservedValue(input, i);
    if (reserved) {
      if (isParticleConnector(reserved.name)) {
        out += resolveParticleConnector(reserved.name, out);
      } else {
        const resolved = resolveInlineValue(reserved.name, context);
        if (resolved.found) {
          out += formatValue(resolved.value);
        } else {
          if (!INLINE_LITERAL_COMMAND_NAMES.has(reserved.name)) {
            pushRoqeetDiagnostic(
              context,
              "UNKNOWN_IDENTIFIER",
              `Unknown inline value @${reserved.name}. Declare it with @state or provide it as a context/built-in value.`,
            );
          }
          out += `@${reserved.name}`;
        }
      }
      i = reserved.end;
      continue;
    }

    out += ch;
  }
  return out;
}

export function evaluateRoqeetExpression(source: string, context: RoqeetRunContext = {}): unknown {
  const parser = new RexParser(tokenizeRex(source), context);
  return parser.parse();
}

function parseBlock(state: ParseState, start: number, nested: boolean): [RoqeetNode[], number] {
  const nodes: RoqeetNode[] = [];
  let i = start;

  while (i < state.lines.length) {
    const line = state.lines[i]!;
    const trimmed = line.text.trim();
    if (!trimmed) {
      i++;
      continue;
    }
    if (trimmed === "}") {
      if (!nested) {
        state.errors.push({
          line: line.line,
          code: "UNEXPECTED_CLOSE",
          message: "Block close marker appears without a matching open marker.",
        });
      }
      return [nodes, i + 1];
    }
    if (isElseLine(trimmed)) {
      return [nodes, i];
    }

    const parsed = parseLine(state, i);
    if (parsed) {
      nodes.push(parsed.node);
      i = parsed.next;
      continue;
    }

    nodes.push({
      type: "Text",
      text: line.text.trim().replace(LEADING_BULLET_RE, ""),
      line: line.line,
      surface: "mixed",
    });
    i++;
  }

  if (nested) {
    const last = state.lines[state.lines.length - 1];
    state.errors.push({
      line: last?.line ?? 1,
      code: "UNCLOSED_BLOCK",
      message: "Block is missing a closing } marker.",
    });
  }
  return [nodes, i];
}

function parseLiteralBlock(state: ParseState, start: number): [RoqeetNode[], number] {
  const nodes: RoqeetNode[] = [];
  let i = start;

  while (i < state.lines.length) {
    const line = state.lines[i]!;
    const trimmed = line.text.trim();
    if (trimmed === "}") return [nodes, i + 1];
    if (trimmed) {
      nodes.push({
        type: "Text",
        text: escapePromptCommandExamples(trimmed.replace(LEADING_BULLET_RE, "")),
        line: line.line,
        surface: "mixed",
      });
    }
    i++;
  }

  const last = state.lines[state.lines.length - 1];
  state.errors.push({
    line: last?.line ?? 1,
    code: "UNCLOSED_BLOCK",
    message: "Block is missing a closing } marker.",
  });
  return [nodes, i];
}

function escapePromptCommandExamples(text: string): string {
  return text.replace(/@([\p{L}_][\p{L}\p{N}_-]*)/gu, (match, name: string) =>
    KNOWN_COMMAND_NAMES.has(name) ? `@@${name}` : match,
  );
}

function parseLine(state: ParseState, index: number): { node: RoqeetNode; next: number } | null {
  const line = state.lines[index]!;
  const trimmed = line.text.trim();
  if (!trimmed.startsWith("@")) return null;

  const command = readCommand(trimmed);
  if (!command) return null;
  const surface = command.surface;
  const rest = trimmed.slice(command.end).trim();

  if (!isKnownCommand(command.name)) {
    const unsupported = UNSUPPORTED_DOCUMENTED_COMMANDS.get(command.name);
    if (!unsupported) return null;
    state.errors.push({
      line: line.line,
      code: "INVALID_COMMAND",
      message: `@${command.name} is documented as ${unsupported.status}, but it is not supported by the current .rqt runtime. ${unsupported.guidance}`,
    });
    if (rest === "{" || lineHasOpenBrace(rest)) {
      const [, next] = parseLiteralBlock(state, index + 1);
      return { node: { type: "Comment", text: trimmed, line: line.line, surface }, next };
    }
    return { node: { type: "Comment", text: trimmed, line: line.line, surface }, next: index + 1 };
  }

  if (COMMANDS.game.has(command.name)) {
    const meta = parseGameMetaDeclaration(state, line.line, rest);
    if (!meta) return { node: { type: "Comment", text: trimmed, line: line.line, surface }, next: index + 1 };
    return { node: { type: "GameMeta", values: meta, line: line.line, surface }, next: index + 1 };
  }

  if (COMMANDS.state.has(command.name)) {
    const field = parseStateSchemaDeclaration(state, line.line, rest);
    if (!field) return { node: { type: "Comment", text: trimmed, line: line.line, surface }, next: index + 1 };
    return { node: { type: "StateSchema", field, line: line.line, surface }, next: index + 1 };
  }

  if (COMMANDS.comment.has(command.name)) {
    if (rest === "{") {
      const [body, next] = parseBlock(state, index + 1, true);
      return {
        node: {
          type: "Comment",
          text: body.map((node) => ("text" in node ? node.text : "")).join("\n"),
          line: line.line,
          surface,
        },
        next,
      };
    }
    return { node: { type: "Comment", text: rest, line: line.line, surface }, next: index + 1 };
  }

  if (COMMANDS.set.has(command.name)) {
    const assignment = parseAssignment(rest);
    if (!assignment) {
      state.errors.push({
        line: line.line,
        code: "INVALID_ASSIGNMENT",
        message: "@set requires a target and an expression.",
      });
      return { node: { type: "Comment", text: trimmed, line: line.line, surface }, next: index + 1 };
    }
    return {
      node: {
        type: "Set",
        target: assignment.target,
        expression: assignment.expression,
        line: line.line,
        surface,
      },
      next: index + 1,
    };
  }

  if (COMMANDS.increase.has(command.name) || COMMANDS.decrease.has(command.name)) {
    const change = parseChange(rest);
    if (!change) {
      state.errors.push({
        line: line.line,
        code: "INVALID_ASSIGNMENT",
        message: "@increase/@decrease requires a target and an amount.",
      });
      return { node: { type: "Comment", text: trimmed, line: line.line, surface }, next: index + 1 };
    }
    return {
      node: {
        type: "Change",
        op: COMMANDS.increase.has(command.name) ? "increase" : "decrease",
        target: change.target,
        amount: change.amount,
        ...(change.min ? { min: change.min } : {}),
        ...(change.max ? { max: change.max } : {}),
        line: line.line,
        surface,
      },
      next: index + 1,
    };
  }

  if (COMMANDS.push.has(command.name)) {
    const pushed = parsePush(rest);
    if (!pushed) {
      state.errors.push({
        line: line.line,
        code: "INVALID_ASSIGNMENT",
        message: "@push requires a target and a value.",
      });
      return { node: { type: "Comment", text: trimmed, line: line.line, surface }, next: index + 1 };
    }
    return {
      node: {
        type: "Push",
        target: pushed.target,
        value: pushed.value,
        line: line.line,
        surface,
      },
      next: index + 1,
    };
  }

  if (COMMANDS.unset.has(command.name)) {
    const target = rest.trim();
    if (!target) {
      state.errors.push({
        line: line.line,
        code: "INVALID_ASSIGNMENT",
        message: "@unset requires a target.",
      });
      return { node: { type: "Comment", text: trimmed, line: line.line, surface }, next: index + 1 };
    }
    return {
      node: {
        type: "Unset",
        target,
        line: line.line,
        surface,
      },
      next: index + 1,
    };
  }

  if (COMMANDS.if.has(command.name)) {
    const condition = stripTrailingOpenBrace(rest);
    const errorCount = state.errors.length;
    const [thenNodes, afterThen] = parseBlock(state, index + 1, true);
    const partial = state.errors.slice(errorCount).some((error) => error.code === "UNCLOSED_BLOCK");
    let next = afterThen;
    const elseIf: RoqeetElseIfClause[] = [];
    let elseNodes: RoqeetNode[] | undefined;

    while (state.lines[next] && isElseLine(state.lines[next]!.text.trim())) {
      const elseLine = state.lines[next]!;
      const elseTrimmed = elseLine.text.trim();
      const elseCommand = readCommand(elseTrimmed);
      const elseRest = elseCommand ? elseTrimmed.slice(elseCommand.end).trim() : "";
      const elseCondition = stripTrailingOpenBrace(elseRest);
      if (elseCondition) {
        const [body, afterElseIf] = parseBlock(state, next + 1, true);
        elseIf.push({ condition: elseCondition, then: body });
        next = afterElseIf;
        continue;
      }
      if (elseRest.startsWith("{") || elseRest === "") {
        const [body, afterElse] = parseBlock(state, next + 1, true);
        elseNodes = body;
        next = afterElse;
      }
      break;
    }
    const node: RoqeetIfNode = {
      type: "If",
      condition,
      then: thenNodes,
      line: line.line,
      surface,
    };
    if (partial) node.partial = true;
    if (elseIf.length) node.elseIf = elseIf;
    if (elseNodes) node.else = elseNodes;
    return { node, next };
  }

  if (COMMANDS.random.has(command.name)) {
    const [items, next] = parseBlock(state, index + 1, true);
    return { node: { type: "Random", items, line: line.line, surface }, next };
  }

  if (COMMANDS.prompt.has(command.name)) {
    const promptName = readQuoted(rest)?.value ?? stripTrailingOpenBrace(rest).trim();
    const name = promptName || "main";
    // Prompt blocks are authoring text, not executable RoQeet command blocks.
    // This keeps examples like "@set trust = 5" or "@roll d20 dc=12" from
    // mutating imported game state while still allowing inline interpolation.
    const [body, next] = parseLiteralBlock(state, index + 1);
    return { node: { type: "Prompt", name, body, line: line.line, surface }, next };
  }

  if (COMMANDS.dialogue.has(command.name)) {
    const speaker = readQuoted(rest)?.value ?? stripTrailingOpenBrace(rest).trim();
    const [body, next] = parseBlock(state, index + 1, true);
    return { node: { type: "Dialogue", speaker, body, line: line.line, surface }, next };
  }

  if (COMMANDS.button.has(command.name)) {
    const button = parseButton(rest);
    return {
      node: {
        type: "Button",
        label: button.label,
        action: button.action,
        line: line.line,
        surface,
      },
      next: index + 1,
    };
  }

  if (COMMANDS.choices.has(command.name)) {
    const [body, next] = parseBlock(state, index + 1, true);
    const items = body
      .filter((node): node is RoqeetTextNode => node.type === "Text")
      .map((node) => parseChoiceItem(node.text));
    return { node: { type: "Choices", items, line: line.line, surface }, next };
  }

  if (COMMANDS.image.has(command.name)) {
    const asset = readQuoted(rest)?.value ?? rest;
    return { node: { type: "Image", asset, line: line.line, surface }, next: index + 1 };
  }

  if (COMMANDS.scene.has(command.name)) {
    const parsed = parseScene(state, line.line, rest);
    const hasBody = rest.endsWith("{") || rest.trim() === "{" || lineHasOpenBrace(rest);
    if (hasBody) {
      const [body, next] = parseBlock(state, index + 1, true);
      return {
        node: { type: "Scene", id: parsed.id, props: parsed.props, body, line: line.line, surface },
        next,
      };
    }
    return {
      node: { type: "Scene", id: parsed.id, props: parsed.props, body: [], line: line.line, surface },
      next: index + 1,
    };
  }

  if (COMMANDS.roll.has(command.name)) {
    const notation = readQuoted(rest)?.value ?? rest.trim();
    if (!notation) {
      state.errors.push({
        line: line.line,
        code: "INVALID_ASSIGNMENT",
        message: "@roll requires a dice notation.",
      });
      return { node: { type: "Comment", text: trimmed, line: line.line, surface }, next: index + 1 };
    }
    return { node: { type: "Roll", notation, line: line.line, surface }, next: index + 1 };
  }

  if (COMMANDS.env.has(command.name)) {
    const [body, next] = parseBlock(state, index + 1, true);
    const values: Record<string, RoqeetValue> = {};
    for (const entry of body) {
      if (entry.type !== "Text") continue;
      const assignment = parseAssignment(entry.text.trim());
      if (!assignment) continue;
      try {
        setSafeRecordValue(values, assignment.target, toRoqeetValue(evaluateRoqeetExpression(assignment.expression)));
      } catch (error) {
        state.errors.push({
          line: entry.line,
          code: error instanceof RoqeetExpressionError ? error.code : "INVALID_EXPRESSION",
          message: error instanceof Error ? error.message : String(error),
        });
      }
    }
    return { node: { type: "Env", values, line: line.line, surface }, next };
  }

  state.errors.push({
    line: line.line,
    code: "INVALID_COMMAND",
    message: `Unknown Roqeet command: @${command.name}`,
  });
  return { node: { type: "Comment", text: trimmed, line: line.line, surface }, next: index + 1 };
}

function readCommand(trimmed: string): { name: string; end: number; surface: RoqeetSurface } | null {
  if (trimmed.charCodeAt(0) !== 64) return null;
  let end = 1;
  while (end < trimmed.length) {
    const ch = trimmed[end]!;
    if (ch === "{" || isWhitespaceChar(ch)) break;
    end++;
  }
  if (end === 1) return null;
  const name = trimmed.slice(1, end);
  const surface: RoqeetSurface = isAscii(name) ? "en" : HANGUL_SCRIPT_RE.test(name) ? "ko" : "mixed";
  return { name, end, surface };
}

function isElseLine(trimmed: string): boolean {
  const command = readCommand(trimmed);
  return !!command && COMMANDS.else.has(command.name);
}

function isKnownCommand(name: string): boolean {
  return KNOWN_COMMAND_NAMES.has(name);
}

function isAscii(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    if (value.charCodeAt(i) > 0x7f) return false;
  }
  return true;
}

function isWhitespaceChar(value: string): boolean {
  return value === " " || value === "\t" || value === "\n" || value === "\r" || value === "\f";
}

function setSafeRecordValue<T>(target: Record<string, T>, key: string, value: T): void {
  if (isUnsafeObjectKey(key)) return;
  target[key] = value;
}

function isUnsafeObjectKey(key: string): boolean {
  return UNSAFE_OBJECT_KEYS.has(key);
}

function stripTrailingOpenBrace(value: string): string {
  let end = value.length;
  while (end > 0 && isWhitespaceChar(value[end - 1]!)) end--;
  if (value[end - 1] === "{") end--;
  while (end > 0 && isWhitespaceChar(value[end - 1]!)) end--;
  return value.slice(0, end).trim();
}

function parseGameMetaDeclaration(
  state: ParseState,
  line: number,
  input: string,
): Record<string, RoqeetValue> | null {
  const parsed = parseAttributeInput(input);
  if (parsed.positionals.length) {
    pushDeclarationError(state, line, "@game uses key=value attributes. Example: @game title=\"Sample Game\" description=\"Short pitch\".");
    return null;
  }

  const values: Record<string, RoqeetValue> = {};
  for (const attr of parsed.attributes) {
    const canonical = GAME_META_ATTRS.get(attr.key);
    if (!canonical) {
      pushDeclarationError(
        state,
        line,
        `@game does not support "${attr.key}" yet. Stable metadata keys are title, description, and icon.`,
      );
      return null;
    }
    if (typeof attr.value !== "string" || !attr.value.trim()) {
      pushDeclarationError(state, line, `@game ${attr.key} must be a non-empty string.`);
      return null;
    }
    values[canonical] = attr.value.trim();
  }

  if (typeof values["title"] !== "string") {
    pushDeclarationError(state, line, "@game requires title=\"...\".");
    return null;
  }
  return values;
}

function parseStateSchemaDeclaration(
  state: ParseState,
  line: number,
  input: string,
): RoqeetStateDeclaration | null {
  const parsed = parseAttributeInput(input);
  const key = parsed.positionals[0]?.trim();
  const rawType = parsed.positionals[1]?.trim();
  if (!key || !rawType) {
    pushDeclarationError(
      state,
      line,
      "@state requires a key and a stable type. Example: @state hp number initial=100 min=0 max=100 mutableBy=ai.",
    );
    return null;
  }
  if (parsed.positionals.length > 2) {
    pushDeclarationError(state, line, "@state accepts only two positional values: key and type. Put other values in key=value attributes.");
    return null;
  }

  const valueType = normalizeStateValueType(rawType);
  if (!valueType) {
    pushDeclarationError(
      state,
      line,
      `@state type "${rawType}" is not Stable yet. Stable types are number, string, boolean, and enum.`,
    );
    return null;
  }

  const attrs = new Map<string, RoqeetValue>();
  for (const attr of parsed.attributes) {
    const canonical = STATE_ATTRS.get(attr.key);
    if (!canonical) {
      pushDeclarationError(
        state,
        line,
        `@state does not support "${attr.key}" yet. Stable attributes are initial, min, max, values, mutableBy, mutable, derived, transient, and label.`,
      );
      return null;
    }
    attrs.set(canonical, attr.value);
  }

  if (!attrs.has("initial")) {
    pushDeclarationError(state, line, "@state requires initial=... so the .rqg initialState is explicit.");
    return null;
  }
  const initial = attrs.get("initial")!;
  if (!stateInitialMatchesType(initial, valueType)) {
    pushDeclarationError(state, line, `@state ${key} initial value does not match type ${valueType}.`);
    return null;
  }

  const field: RoqeetStateDeclaration = { key, valueType, initial };
  const min = attrs.get("min");
  const max = attrs.get("max");
  if (min !== undefined) {
    if (typeof min !== "number") {
      pushDeclarationError(state, line, "@state min must be a number.");
      return null;
    }
    field.min = min;
  }
  if (max !== undefined) {
    if (typeof max !== "number") {
      pushDeclarationError(state, line, "@state max must be a number.");
      return null;
    }
    field.max = max;
  }
  if ((min !== undefined || max !== undefined) && valueType !== "number") {
    pushDeclarationError(state, line, "@state min/max are Stable only for number fields.");
    return null;
  }
  if (field.min !== undefined && field.max !== undefined && field.min > field.max) {
    pushDeclarationError(state, line, "@state min cannot be greater than max.");
    return null;
  }

  const values = attrs.get("values");
  if (values !== undefined) {
    if (valueType !== "enum") {
      pushDeclarationError(state, line, "@state values=... is Stable only for enum fields.");
      return null;
    }
    const enumValues = parseEnumValues(values);
    if (!enumValues.length) {
      pushDeclarationError(state, line, "@state enum values must include at least one value.");
      return null;
    }
    if (typeof initial !== "string" || !enumValues.includes(initial)) {
      pushDeclarationError(state, line, "@state enum initial must be one of values=...");
      return null;
    }
    field.values = enumValues;
  } else if (valueType === "enum") {
    pushDeclarationError(state, line, "@state enum requires values=\"a,b,c\".");
    return null;
  }

  const mutableBy = attrs.get("mutableBy");
  if (mutableBy !== undefined) {
    if (typeof mutableBy !== "string" || !STATE_MUTABLE_BY.has(mutableBy as RoqeetStateMutableBy)) {
      pushDeclarationError(state, line, "@state mutableBy must be ai, engine, or readonly.");
      return null;
    }
    field.mutableBy = mutableBy as RoqeetStateMutableBy;
  }
  const mutable = attrs.get("mutable");
  if (mutable !== undefined) {
    if (typeof mutable !== "boolean") {
      pushDeclarationError(state, line, "@state mutable must be true or false.");
      return null;
    }
    field.mutable = mutable;
  }
  const derived = attrs.get("derived");
  if (derived !== undefined) {
    if (typeof derived !== "boolean") {
      pushDeclarationError(state, line, "@state derived must be true or false.");
      return null;
    }
    field.derived = derived;
  }
  const transient = attrs.get("transient");
  if (transient !== undefined) {
    if (typeof transient !== "boolean") {
      pushDeclarationError(state, line, "@state transient must be true or false.");
      return null;
    }
    field.transient = transient;
  }
  const label = attrs.get("label");
  if (label !== undefined) {
    if (typeof label !== "string") {
      pushDeclarationError(state, line, "@state label must be a string.");
      return null;
    }
    field.label = label;
  }

  return field;
}

interface ParsedAttributes {
  positionals: string[];
  attributes: Array<{ key: string; value: RoqeetValue }>;
}

function parseAttributeInput(input: string, state?: ParseState, line?: number): ParsedAttributes {
  const { tokens, diagnostics } = splitAttributeTokens(input);
  if (state && line !== undefined) {
    for (const message of diagnostics) pushDeclarationError(state, line, message);
  }
  const positionals: string[] = [];
  const attributes: Array<{ key: string; value: RoqeetValue }> = [];
  for (const token of tokens) {
    const eq = token.indexOf("=");
    if (eq < 0) {
      positionals.push(stripAttributeQuotes(token));
      continue;
    }
    if (eq === 0) {
      if (state && line !== undefined) pushDeclarationError(state, line, "Attribute is missing a key before '='.");
      continue;
    }
    const key = token.slice(0, eq).trim();
    const value = token.slice(eq + 1).trim();
    if (!key) {
      if (state && line !== undefined) pushDeclarationError(state, line, "Attribute is missing a key before '='.");
      continue;
    }
    if (!value) {
      if (state && line !== undefined) pushDeclarationError(state, line, `Attribute "${key}" is missing a value after '='.`);
      continue;
    }
    attributes.push({ key, value: parseAttributeValue(value) });
  }
  return { positionals, attributes };
}

function splitAttributeTokens(input: string): { tokens: string[]; diagnostics: string[] } {
  const tokens: string[] = [];
  const diagnostics: string[] = [];
  let i = 0;
  while (i < input.length) {
    while (i < input.length && isWhitespaceChar(input[i]!)) i++;
    if (i >= input.length) break;
    let token = "";
    let quote = "";
    while (i < input.length) {
      const ch = input[i]!;
      if (quote) {
        token += ch;
        if (ch === "\\" && i + 1 < input.length) {
          token += input[i + 1]!;
          i += 2;
          continue;
        }
        if (ch === quote) quote = "";
        i++;
        continue;
      }
      if (ch === "\"" || ch === "'") {
        quote = ch;
        token += ch;
        i++;
        continue;
      }
      if (isWhitespaceChar(ch)) break;
      token += ch;
      i++;
    }
    if (quote) diagnostics.push("Unterminated quoted attribute value.");
    if (token) tokens.push(token);
    while (i < input.length && isWhitespaceChar(input[i]!)) i++;
  }
  return { tokens, diagnostics };
}

function parseAttributeValue(raw: string): RoqeetValue {
  const value = raw.trim();
  if ((value.startsWith("\"") && value.endsWith("\"")) || (value.startsWith("'") && value.endsWith("'"))) {
    return unescapeQuoted(value.slice(1, -1));
  }
  if (value === "true" || value === "\uCC38") return true;
  if (value === "false" || value === "\uAC70\uC9D3") return false;
  if (value === "null" || value === "\uC5C6\uC74C") return null;
  if (/^-?\d+(?:\.\d+)?$/.test(value)) return Number(value);
  return value;
}

function stripAttributeQuotes(value: string): string {
  const trimmed = value.trim();
  if ((trimmed.startsWith("\"") && trimmed.endsWith("\"")) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    return unescapeQuoted(trimmed.slice(1, -1));
  }
  return trimmed;
}

function unescapeQuoted(value: string): string {
  return value.replace(/\\(["'\\nrt])/g, (_match, ch: string) => {
    if (ch === "n") return "\n";
    if (ch === "r") return "\r";
    if (ch === "t") return "\t";
    return ch;
  });
}

function normalizeStateValueType(value: string): RoqeetStateValueType | undefined {
  switch (value) {
    case "number":
    case "\uC22B\uC790":
      return "number";
    case "string":
    case "text":
    case "\uBB38\uC790\uC5F4":
      return "string";
    case "boolean":
    case "bool":
    case "\uCC38\uAC70\uC9D3":
      return "boolean";
    case "enum":
    case "\uC5F4\uAC70":
      return "enum";
    default:
      return undefined;
  }
}

function stateInitialMatchesType(value: RoqeetValue, valueType: RoqeetStateValueType): boolean {
  switch (valueType) {
    case "number":
      return typeof value === "number" && Number.isFinite(value);
    case "boolean":
      return typeof value === "boolean";
    case "string":
    case "enum":
      return typeof value === "string";
  }
}

function parseEnumValues(value: RoqeetValue): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string").map((item) => item.trim()).filter(Boolean);
  }
  if (typeof value !== "string") return [];
  return value.split(",").map((item) => item.trim()).filter(Boolean);
}

function pushDeclarationError(state: ParseState, line: number, message: string): void {
  state.errors.push({ line, code: "INVALID_ASSIGNMENT", message });
}

function parseAssignment(input: string): { target: string; expression: string } | null {
  const eq = input.indexOf("=");
  if (eq >= 0) {
    const target = input.slice(0, eq).trim();
    const expression = input.slice(eq + 1).trim();
    return target && expression ? { target, expression } : null;
  }
  const trimmed = input.trim();
  let split = 0;
  while (split < trimmed.length && !isWhitespaceChar(trimmed[split]!)) split++;
  if (split === 0 || split >= trimmed.length) return null;
  let expressionStart = split;
  while (expressionStart < trimmed.length && isWhitespaceChar(trimmed[expressionStart]!)) {
    expressionStart++;
  }
  const target = trimmed.slice(0, split);
  const expression = trimmed.slice(expressionStart).trim();
  return target && expression ? { target, expression } : null;
}

function parseChange(input: string): { target: string; amount: string; min?: string; max?: string } | null {
  const parts = input.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return null;
  const target = parts[0]!;
  const amountIndex = parts[1] === "by" ? 2 : 1;
  const amount = parts[amountIndex];
  if (!amount) return null;

  const out: { target: string; amount: string; min?: string; max?: string } = { target, amount };
  for (let i = amountIndex + 1; i < parts.length - 1; i++) {
    const key = parts[i];
    const value = parts[i + 1];
    if (!value) continue;
    if (key === "\uCD5C\uC18C" || key === "min") {
      out.min = value;
      i++;
    } else if (key === "\uCD5C\uB300" || key === "max") {
      out.max = value;
      i++;
    }
  }
  return out;
}

function parsePush(input: string): { target: string; value: string } | null {
  const trimmed = input.trim();
  let split = 0;
  while (split < trimmed.length && !isWhitespaceChar(trimmed[split]!)) split++;
  if (split === 0 || split >= trimmed.length) return null;
  let valueStart = split;
  while (valueStart < trimmed.length && isWhitespaceChar(trimmed[valueStart]!)) valueStart++;
  const target = trimmed.slice(0, split);
  const value = trimmed.slice(valueStart).trim();
  return target && value ? { target, value } : null;
}

function parseButton(input: string): { label: string; action: string } {
  const quoted = readQuoted(input);
  if (!quoted) return { label: input.trim(), action: "" };
  const rest = input.slice(quoted.end).trim();
  const action = rest.startsWith("->") ? rest.slice(2).trim() : rest;
  return { label: quoted.value, action };
}

function parseChoiceItem(input: string): RoqeetChoiceItem {
  const trimmed = input.trim();
  const text = trimmed.startsWith("-") ? trimmed.slice(1).trimStart() : trimmed;
  const arrow = text.indexOf("->");
  if (arrow >= 0) {
    return { label: text.slice(0, arrow).trim(), action: text.slice(arrow + 2).trim() };
  }
  return { label: text };
}

function parseScene(state: ParseState, line: number, input: string): { id: string; props: Record<string, string> } {
  const withoutBrace = stripTrailingOpenBrace(input);
  const parsed = parseAttributeInput(withoutBrace, state, line);
  const props: Record<string, string> = {};
  const [firstPosition, ...extraPositionals] = parsed.positionals;
  const id = firstPosition?.trim() || "scene";

  for (const positional of extraPositionals) {
    if (positional.trim()) pushDeclarationError(state, line, `Malformed scene attribute token "${positional}". Use key=value.`);
  }

  for (const attr of parsed.attributes) {
    const key = SCENE_ATTRS.get(attr.key) ?? attr.key;
    const value = sceneAttributeValue(attr.value);
    if (value === undefined) {
      pushDeclarationError(state, line, `Scene attribute "${attr.key}" must be a scalar value.`);
      continue;
    }
    setSafeRecordValue(props, key, value);
  }
  return { id, props };
}

function sceneAttributeValue(value: RoqeetValue): string | undefined {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return undefined;
}

function lineHasOpenBrace(input: string): boolean {
  let index = input.length - 1;
  while (index >= 0 && isWhitespaceChar(input[index]!)) index--;
  return input[index] === "{";
}

function readQuoted(input: string): { value: string; end: number } | null {
  const trimmedStart = input.search(/\S/);
  if (trimmedStart < 0 || input[trimmedStart] !== "\"") return null;
  let value = "";
  for (let i = trimmedStart + 1; i < input.length; i++) {
    const ch = input[i];
    if (ch === "\\" && i + 1 < input.length) {
      value += input[i + 1];
      i++;
      continue;
    }
    if (ch === "\"") return { value, end: i + 1 };
    value += ch;
  }
  return null;
}

function toJsonNode(node: RoqeetNode): RoqeetJsonNode {
  switch (node.type) {
    case "Text":
      return { type: "text", text: node.text };
    case "Set":
      return { type: "set", target: node.target, expression: { rex: node.expression } };
    case "Change": {
      const out: Extract<RoqeetJsonNode, { type: "change" }> = {
        type: "change",
        op: node.op,
        target: node.target,
        amount: { rex: node.amount },
      };
      if (node.min) out.min = { rex: node.min };
      if (node.max) out.max = { rex: node.max };
      return out;
    }
    case "Push":
      return { type: "push", target: node.target, value: { rex: node.value } };
    case "Unset":
      return { type: "unset", target: node.target };
    case "If": {
      const out: RoqeetJsonNode = {
        type: "if",
        condition: { rex: node.condition },
        then: node.then.map(toJsonNode),
      };
      if (node.elseIf) {
        out.elseIf = node.elseIf.map((clause) => ({
          condition: { rex: clause.condition },
          then: clause.then.map(toJsonNode),
        }));
      }
      if (node.else) out.else = node.else.map(toJsonNode);
      return out;
    }
    case "Random":
      return { type: "random", items: node.items.map(toJsonNode) };
    case "Prompt":
      return { type: "prompt", name: node.name, body: node.body.map(toJsonNode) };
    case "Dialogue":
      return { type: "dialogue", speaker: node.speaker, body: node.body.map(toJsonNode) };
    case "Button":
      return { type: "button", label: node.label, action: node.action };
    case "Choices":
      return { type: "choices", items: node.items };
    case "Image":
      return { type: "image", asset: node.asset };
    case "Scene":
      return { type: "scene", id: node.id, props: node.props, body: node.body.map(toJsonNode) };
    case "Roll":
      return { type: "roll", notation: node.notation };
    case "Comment":
      return { type: "comment", text: node.text };
    case "Env":
      return { type: "env", values: node.values };
    case "GameMeta":
      return { type: "game", values: node.values };
    case "StateSchema":
      return { type: "state", field: node.field };
  }
}

interface RuntimeState {
  context: RoqeetRunContext;
  errors: RoqeetParseError[];
  state: Record<string, unknown>;
  text: string[];
  prompts: RoqeetPromptOutput[];
  ui: RoqeetUiOutput[];
  media: RoqeetMediaOutput[];
  dialogues: RoqeetDialogueOutput[];
  scenes: RoqeetSceneOutput[];
  rolls: RoqeetRollOutput[];
}

type OutputTarget = "text" | { prompt: string[] };

function executeNodes(nodes: RoqeetNode[], runtime: RuntimeState, target: OutputTarget): void {
  for (const node of nodes) executeNode(node, runtime, target);
}

function executeNode(node: RoqeetNode, runtime: RuntimeState, target: OutputTarget): void {
  switch (node.type) {
    case "Text":
      pushText(renderRoqeetText(node.text, runtimeContext(runtime, node.line)), runtime, target);
      break;
    case "Set":
      setSafeRecordValue(runtime.state, node.target, evaluateRuntimeExpression(node.expression, runtime, node.line));
      break;
    case "Change": {
      const current = toNumber(getPath(runtime.state, node.target));
      const amount = toNumber(evaluateRuntimeExpression(node.amount, runtime, node.line));
      let next = node.op === "increase" ? current + amount : current - amount;
      if (node.min !== undefined) {
        next = Math.max(next, toNumber(evaluateRuntimeExpression(node.min, runtime, node.line)));
      }
      if (node.max !== undefined) {
        next = Math.min(next, toNumber(evaluateRuntimeExpression(node.max, runtime, node.line)));
      }
      setSafeRecordValue(runtime.state, node.target, next);
      break;
    }
    case "Push": {
      const current = getPath(runtime.state, node.target);
      const arr = Array.isArray(current) ? [...current] : [];
      arr.push(evaluateRuntimeExpression(node.value, runtime, node.line));
      setSafeRecordValue(runtime.state, node.target, arr);
      break;
    }
    case "Unset":
      delete runtime.state[node.target];
      break;
    case "If":
      executeNodes(resolveIfBranch(node, runtime), runtime, target);
      break;
    case "Random": {
      if (node.items.length === 0) break;
      const random = runtime.context.random ?? Math.random;
      const index = Math.min(node.items.length - 1, Math.floor(random() * node.items.length));
      const picked = node.items[index];
      if (picked) executeNode(picked, runtime, target);
      break;
    }
    case "Prompt": {
      const lines: string[] = [];
      executeNodes(node.body, runtime, { prompt: lines });
      runtime.prompts.push({ name: node.name, content: lines.join("\n") });
      break;
    }
    case "Dialogue": {
      const lines: string[] = [];
      executeNodes(node.body, runtime, { prompt: lines });
      const context = runtimeContext(runtime, node.line);
      const speaker = renderRoqeetText(node.speaker, context);
      const content = lines.join("\n");
      runtime.dialogues.push({ speaker, content });
      pushText(speaker ? `${speaker}: ${content}` : content, runtime, target);
      break;
    }
    case "Button":
      runtime.ui.push({
        kind: "button",
        label: renderRoqeetText(node.label, runtimeContext(runtime, node.line)),
        action: node.action,
      });
      break;
    case "Choices": {
      const context = runtimeContext(runtime, node.line);
      for (const item of node.items) {
        const label = renderRoqeetText(item.label, context);
        runtime.ui.push({
          kind: "choice",
          label,
          action: renderRoqeetText(item.action ?? item.label, context),
        });
      }
      break;
    }
    case "Image":
      runtime.media.push({ kind: "image", asset: node.asset });
      break;
    case "Scene": {
      const context = runtimeContext(runtime, node.line);
      runtime.scenes.push({
        id: renderRoqeetText(node.id, context),
        props: renderProps(node.props, context),
      });
      executeNodes(node.body, runtime, target);
      break;
    }
    case "Roll": {
      const result = rollRoqeetDice(node.notation, runtime.context.random ?? Math.random);
      runtime.rolls.push({ notation: node.notation, result });
      runtime.state["lastRoll"] = result;
      runtime.state["lastRollNotation"] = node.notation;
      break;
    }
    case "Comment":
    case "Env":
    case "GameMeta":
      break;
    case "StateSchema":
      if (!isUnsafeObjectKey(node.field.key) && !Object.prototype.hasOwnProperty.call(runtime.state, node.field.key)) {
        setSafeRecordValue(runtime.state, node.field.key, node.field.initial);
      }
      break;
  }
}

function resolveIfBranch(node: RoqeetIfNode, runtime: RuntimeState): RoqeetNode[] {
  if (node.partial) return node.then;
  if (isTruthy(evaluateRuntimeExpression(node.condition, runtime, node.line))) {
    return node.then;
  }
  for (const clause of node.elseIf ?? []) {
    if (isTruthy(evaluateRuntimeExpression(clause.condition, runtime, node.line))) {
      return clause.then;
    }
  }
  return node.else ?? [];
}

function renderProps(props: Record<string, string>, context: RoqeetRunContext): Record<string, string> {
  return Object.fromEntries(Object.entries(props).map(([key, value]) => [key, renderRoqeetText(value, context)]));
}

function evaluateRuntimeExpression(source: string, runtime: RuntimeState, line: number): unknown {
  const context = runtimeContext(runtime, line);
  try {
    return evaluateRoqeetExpression(source, context);
  } catch (error) {
    pushRoqeetExpressionDiagnostic(context, error, source);
    return null;
  }
}

function runtimeContext(runtime: RuntimeState, line?: number): RoqeetRunContext {
  return {
    ...runtime.context,
    diagnostics: runtime.errors,
    diagnosticLine: line,
    state: runtime.state,
    values: {
      ...(runtime.context.values ?? {}),
      ...runtime.state,
      char: runtime.context.char,
      user: runtime.context.user,
    },
  };
}

function pushText(text: string, runtime: RuntimeState, target: OutputTarget): void {
  if (!text) return;
  if (target === "text") runtime.text.push(text);
  else target.prompt.push(text);
}

function readInlineExpression(input: string, start: number): { expression: string; end: number } | null {
  let depth = 1;
  let expression = "";
  for (let i = start; i < input.length; i++) {
    const ch = input[i];
    if (ch === "(") depth++;
    if (ch === ")") {
      depth--;
      if (depth === 0) return { expression, end: i };
    }
    expression += ch;
  }
  return null;
}

const RESERVED_INLINE_NAMES = [
  "\uCE90\uB9AD\uD130\uBA85",
  "\uC0AC\uC6A9\uC790\uBA85",
  "\uC720\uC800\uBA85",
  "\uD604\uC7AC\uB0A0\uC9DC\uC2DC\uAC04",
  "\uD604\uC7AC\uB0A0\uC9DC",
  "\uD604\uC7AC\uC2DC\uAC04",
  "\uCE90\uB9AD\uD130",
  "\uC720\uC800",
  "\uC740\uB294",
  "\uC774\uAC00",
  "\uC744\uB97C",
  "\uB0A0\uC9DC",
  "\uC2DC\uAC04",
  "\uC694\uC77C",
  "\uC904\uBC14\uAFC8",
  "character_name",
  "current_datetime",
  "current_date",
  "current_time",
  "user_name",
  "weekday",
  "newline",
  "char",
  "user",
  "date",
  "time",
] as const;

const SORTED_RESERVED_INLINE_NAMES = [...RESERVED_INLINE_NAMES].sort((a, b) => b.length - a.length);

function readReservedValue(input: string, start: number): { name: string; end: number } | null {
  for (const name of SORTED_RESERVED_INLINE_NAMES) {
    if (input.startsWith(`@${name}`, start)) {
      const after = input[start + name.length + 1] ?? "";
      if (ALPHA_RE.test(name) && ASCII_WORD_CHAR_RE.test(after)) continue;
      return { name, end: start + name.length };
    }
  }
  const match = INLINE_IDENTIFIER_RE.exec(input.slice(start));
  if (!match) return null;
  return { name: match[1]!, end: start + match[0].length - 1 };
}

function pushRoqeetDiagnostic(context: RoqeetRunContext, code: RoqeetParseError["code"], message: string): void {
  context.diagnostics?.push({
    line: context.diagnosticLine ?? 0,
    code,
    message,
  });
}

function pushRoqeetExpressionDiagnostic(context: RoqeetRunContext, error: unknown, source: string): void {
  if (error instanceof RoqeetExpressionError) {
    pushRoqeetDiagnostic(context, error.code, `${source}: ${error.message}`);
    return;
  }
  pushRoqeetDiagnostic(
    context,
    "INVALID_EXPRESSION",
    `${source}: ${error instanceof Error ? error.message : String(error)}`,
  );
}

function resolveInlineValue(name: string, context: RoqeetRunContext): { found: boolean; value?: unknown } {
  switch (name) {
    case "\uC720\uC800":
    case "\uC720\uC800\uBA85":
    case "\uC0AC\uC6A9\uC790\uBA85":
    case "user":
    case "user_name":
      return { found: true, value: context.user ?? "" };
    case "\uCE90\uB9AD\uD130":
    case "\uCE90\uB9AD\uD130\uBA85":
    case "char":
    case "character_name":
      return { found: true, value: context.char ?? "" };
    case "\uB0A0\uC9DC":
    case "\uD604\uC7AC\uB0A0\uC9DC":
    case "date":
    case "current_date":
      return { found: true, value: new Date(context.now?.() ?? Date.now()).toISOString().slice(0, 10) };
    case "\uC2DC\uAC04":
    case "\uD604\uC7AC\uC2DC\uAC04":
    case "time":
    case "current_time":
      return { found: true, value: new Date(context.now?.() ?? Date.now()).toISOString().slice(11, 16) };
    case "\uD604\uC7AC\uB0A0\uC9DC\uC2DC\uAC04":
    case "current_datetime":
      return { found: true, value: formatRoqeetDateTime(context) };
    case "\uC694\uC77C":
      return { found: true, value: formatRoqeetWeekday(context, "ko") };
    case "weekday":
      return { found: true, value: formatRoqeetWeekday(context, "en") };
    case "\uC904\uBC14\uAFC8":
    case "newline":
      return { found: true, value: "\n" };
    default: {
      const value = getContextValue(context, name);
      return value.found ? value : { found: false };
    }
  }
}

function formatRoqeetDateTime(context: RoqeetRunContext): string {
  return new Date(context.now?.() ?? Date.now()).toISOString().slice(0, 16).replace("T", " ");
}

function formatRoqeetWeekday(context: RoqeetRunContext, locale: "ko" | "en"): string {
  const day = new Date(context.now?.() ?? Date.now()).getUTCDay();
  const ko = ["\uC77C\uC694\uC77C", "\uC6D4\uC694\uC77C", "\uD654\uC694\uC77C", "\uC218\uC694\uC77C", "\uBAA9\uC694\uC77C", "\uAE08\uC694\uC77C", "\uD1A0\uC694\uC77C"];
  const en = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  return locale === "ko" ? ko[day] ?? "" : en[day] ?? "";
}

function isParticleConnector(name: string): boolean {
  return name === "\uC740\uB294" || name === "\uC774\uAC00" || name === "\uC744\uB97C";
}

function resolveParticleConnector(name: string, renderedText: string): string {
  const hasBatchim = hasFinalConsonant(readLastMeaningfulChar(renderedText));
  if (name === "\uC740\uB294") return hasBatchim ? "\uC740" : "\uB294";
  if (name === "\uC774\uAC00") return hasBatchim ? "\uC774" : "\uAC00";
  if (name === "\uC744\uB97C") return hasBatchim ? "\uC744" : "\uB97C";
  return "";
}

function readLastMeaningfulChar(value: string): string {
  for (let i = value.length - 1; i >= 0; i--) {
    const char = value[i] ?? "";
    if (!WHITESPACE_RE.test(char) && !PARTICLE_IGNORED_CHARS.has(char)) return char;
  }
  return "";
}

const PARTICLE_IGNORED_CHARS = new Set([
  "\"",
  "'",
  "`",
  ".",
  ",",
  "!",
  "?",
  ";",
  ":",
  "(",
  ")",
  "[",
  "]",
  "{",
  "}",
  "<",
  ">",
]);

function hasFinalConsonant(char: string): boolean {
  if (!char) return false;
  const code = char.charCodeAt(0);
  if (code < 0xac00 || code > 0xd7a3) return false;
  return (code - 0xac00) % 28 !== 0;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(formatValue).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function toRoqeetValue(value: unknown): RoqeetValue {
  if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  if (Array.isArray(value)) return value.map(toRoqeetValue);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, toRoqeetValue(v)]));
  }
  return null;
}

type RexToken =
  | { type: "number"; value: number }
  | { type: "string"; value: string }
  | { type: "identifier"; value: string }
  | { type: "operator"; value: string }
  | { type: "comma"; value: "," }
  | { type: "paren"; value: "(" | ")" }
  | { type: "eof"; value: "" };

function tokenizeRex(source: string): RexToken[] {
  const tokens: RexToken[] = [];
  for (let i = 0; i < source.length; ) {
    const ch = source[i]!;
    if (WHITESPACE_RE.test(ch)) {
      i++;
      continue;
    }
    if (ch === "\u201c" || ch === "\u201d" || ch === "\u2018" || ch === "\u2019") {
      throw new RoqeetExpressionError(
        "INVALID_EXPRESSION",
        "Smart quotes are not valid in RQT expressions. Use normal ASCII double quotes.",
      );
    }
    if (ch === "\"" || ch === "'") {
      const quote = ch;
      let value = "";
      let closed = false;
      i++;
      while (i < source.length) {
        const c = source[i]!;
        if (c === "\\" && i + 1 < source.length) {
          value += source[i + 1]!;
          i += 2;
          continue;
        }
        if (c === quote) {
          i++;
          closed = true;
          break;
        }
        value += c;
        i++;
      }
      if (!closed) {
        throw new RoqeetExpressionError("INVALID_EXPRESSION", "String literal is missing a closing quote.");
      }
      tokens.push({ type: "string", value });
      continue;
    }
    const number = NUMBER_PREFIX_RE.exec(source.slice(i));
    if (number) {
      tokens.push({ type: "number", value: Number(number[0]) });
      i += number[0].length;
      continue;
    }
    const two = source.slice(i, i + 2);
    if ([">=", "<=", "==", "!=", "&&", "||", "**"].includes(two)) {
      tokens.push({ type: "operator", value: two });
      i += 2;
      continue;
    }
    if (["+", "-", "*", "/", "%", "^", ">", "<", "!", "&"].includes(ch)) {
      tokens.push({ type: "operator", value: ch });
      i++;
      continue;
    }
    if (ch === ",") {
      tokens.push({ type: "comma", value: "," });
      i++;
      continue;
    }
    if (ch === "(" || ch === ")") {
      tokens.push({ type: "paren", value: ch });
      i++;
      continue;
    }
    const identifier = REX_IDENTIFIER_PREFIX_RE.exec(source.slice(i));
    if (identifier) {
      tokens.push({ type: "identifier", value: identifier[0] });
      i += identifier[0].length;
      continue;
    }
    throw new RoqeetExpressionError("INVALID_EXPRESSION", `Unexpected token "${ch}" in expression.`);
  }
  tokens.push({ type: "eof", value: "" });
  return tokens;
}

class RexParser {
  private index = 0;

  constructor(
    private readonly tokens: RexToken[],
    private readonly context: RoqeetRunContext,
  ) {}

  parse(): unknown {
    const value = this.parseOr();
    if (this.peek().type !== "eof") {
      throw new RoqeetExpressionError("INVALID_EXPRESSION", `Unexpected token "${this.peek().value}" after expression.`);
    }
    return value;
  }

  private parseOr(): unknown {
    let left = this.parseAnd();
    while (this.matchOperator("||") || this.matchIdentifier("\uB610\uB294") || this.matchIdentifier("or")) {
      const right = this.parseAnd();
      left = isTruthy(left) || isTruthy(right);
    }
    return left;
  }

  private parseAnd(): unknown {
    let left = this.parseEquality();
    while (this.matchOperator("&&") || this.matchIdentifier("\uADF8\uB9AC\uACE0") || this.matchIdentifier("and")) {
      const right = this.parseEquality();
      left = isTruthy(left) && isTruthy(right);
    }
    return left;
  }

  private parseEquality(): unknown {
    let left = this.parseCompare();
    for (;;) {
      if (this.matchOperator("==")) {
        left = left === this.parseCompare();
      } else if (this.matchOperator("!=")) {
        left = left !== this.parseCompare();
      } else {
        return left;
      }
    }
  }

  private parseCompare(): unknown {
    let left = this.parseConcat();
    for (;;) {
      if (this.matchOperator(">=")) {
        left = numberOperand(left, ">=") >= numberOperand(this.parseConcat(), ">=");
      } else if (this.matchOperator("<=")) {
        left = numberOperand(left, "<=") <= numberOperand(this.parseConcat(), "<=");
      } else if (this.matchOperator(">")) {
        left = numberOperand(left, ">") > numberOperand(this.parseConcat(), ">");
      } else if (this.matchOperator("<")) {
        left = numberOperand(left, "<") < numberOperand(this.parseConcat(), "<");
      } else {
        return left;
      }
    }
  }

  private parseConcat(): unknown {
    let left = this.parseTerm();
    while (this.matchOperator("&")) {
      left = `${formatValue(left)}${formatValue(this.parseTerm())}`;
    }
    return left;
  }

  private parseTerm(): unknown {
    let left = this.parseFactor();
    for (;;) {
      if (this.matchOperator("+")) {
        const right = this.parseFactor();
        left = numberOperand(left, "+") + numberOperand(right, "+");
      } else if (this.matchOperator("-")) {
        left = numberOperand(left, "-") - numberOperand(this.parseFactor(), "-");
      } else {
        return left;
      }
    }
  }

  private parseFactor(): unknown {
    let left = this.parsePower();
    for (;;) {
      if (this.matchOperator("*")) {
        left = numberOperand(left, "*") * numberOperand(this.parsePower(), "*");
      } else if (this.matchOperator("/")) {
        left = numberOperand(left, "/") / numberOperand(this.parsePower(), "/");
      } else if (this.matchOperator("%")) {
        left = numberOperand(left, "%") % numberOperand(this.parsePower(), "%");
      } else {
        return left;
      }
    }
  }

  private parsePower(): unknown {
    const left = this.parseUnary();
    if (this.matchOperator("^") || this.matchOperator("**")) {
      return Math.pow(numberOperand(left, "^"), numberOperand(this.parsePower(), "^"));
    }
    return left;
  }

  private parseUnary(): unknown {
    if (this.matchOperator("!")) return !isTruthy(this.parseUnary());
    if (this.matchIdentifier("아님") || this.matchIdentifier("not")) return !isTruthy(this.parseUnary());
    if (this.matchOperator("-")) return -numberOperand(this.parseUnary(), "unary -");
    return this.parsePrimary();
  }

  private parsePrimary(): unknown {
    const token = this.peek();
    if (token.type === "number" || token.type === "string") {
      this.index++;
      return token.value;
    }
    if (token.type === "identifier") {
      this.index++;
      if (this.matchParen("(")) return this.callFunction(token.value, this.parseCallArgs());
      return this.resolveIdentifier(token.value);
    }
    if (this.matchParen("(")) {
      const value = this.parseOr();
      if (!this.matchParen(")")) {
        throw new RoqeetExpressionError("INVALID_EXPRESSION", "Expression group is missing a closing ')'.");
      }
      return value;
    }
    throw new RoqeetExpressionError("INVALID_EXPRESSION", `Unexpected token "${token.value}" in expression.`);
  }

  private parseCallArgs(): unknown[] {
    if (this.matchParen(")")) return [];
    const args: unknown[] = [];
    for (;;) {
      args.push(this.parseOr());
      if (this.matchComma()) continue;
      if (!this.matchParen(")")) {
        throw new RoqeetExpressionError("INVALID_EXPRESSION", "Function call is missing a closing ')'.");
      }
      return args;
    }
  }

  private callFunction(name: string, args: unknown[]): unknown {
    switch (name) {
      case "sqrt":
      case "루트":
        return Math.sqrt(numberOperand(args[0], "sqrt"));
      case "pow":
      case "제곱":
        return Math.pow(numberOperand(args[0], "pow"), numberOperand(args[1], "pow"));
      case "abs":
      case "절댓값":
        return Math.abs(numberOperand(args[0], "abs"));
      case "floor":
      case "내림":
        return Math.floor(numberOperand(args[0], "floor"));
      case "ceil":
      case "올림":
        return Math.ceil(numberOperand(args[0], "ceil"));
      case "round":
      case "반올림":
        return Math.round(numberOperand(args[0], "round"));
      case "min":
      case "최소":
        return Math.min(...args.map((value) => numberOperand(value, "min")));
      case "max":
      case "최대":
        return Math.max(...args.map((value) => numberOperand(value, "max")));
      case "clamp":
      case "범위":
        return Math.min(
          Math.max(numberOperand(args[0], "clamp"), numberOperand(args[1], "clamp")),
          numberOperand(args[2], "clamp"),
        );
      default:
        throw new RoqeetExpressionError("INVALID_EXPRESSION", `Unsupported function "${name}".`);
    }
  }

  private resolveIdentifier(name: string): unknown {
    switch (name) {
      case "참":
      case "true":
        return true;
      case "거짓":
      case "false":
        return false;
      case "없음":
      case "null":
        return null;
      default: {
        const value = getContextValue(this.context, name);
        if (value.found) return value.value;
        throw new RoqeetExpressionError(
          "UNKNOWN_IDENTIFIER",
          `Unknown identifier "${name}". Declare it with @state or pass it as a context value.`,
        );
      }
    }
  }

  private matchOperator(value: string): boolean {
    const token = this.peek();
    if (token.type === "operator" && token.value === value) {
      this.index++;
      return true;
    }
    return false;
  }

  private matchIdentifier(value: string): boolean {
    const token = this.peek();
    if (token.type === "identifier" && token.value === value) {
      this.index++;
      return true;
    }
    return false;
  }

  private matchParen(value: "(" | ")"): boolean {
    const token = this.peek();
    if (token.type === "paren" && token.value === value) {
      this.index++;
      return true;
    }
    return false;
  }

  private matchComma(): boolean {
    const token = this.peek();
    if (token.type === "comma") {
      this.index++;
      return true;
    }
    return false;
  }

  private peek(): RexToken {
    return this.tokens[this.index] ?? { type: "eof", value: "" };
  }
}

function getPath(source: Record<string, unknown> | undefined, path: string): unknown {
  if (!source) return undefined;
  if (Object.prototype.hasOwnProperty.call(source, path)) return source[path];
  let cur: unknown = source;
  for (const part of path.split(".")) {
    if (!cur || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

function getContextValue(context: RoqeetRunContext, path: string): { found: boolean; value?: unknown } {
  const value = findPath(context.values, path);
  if (value.found) return value;
  return findPath(context.state, path);
}

function findPath(source: Record<string, unknown> | undefined, path: string): { found: boolean; value?: unknown } {
  if (!source) return { found: false };
  if (Object.prototype.hasOwnProperty.call(source, path)) return { found: true, value: source[path] };
  return { found: false };
}

function isTruthy(value: unknown): boolean {
  if (value === false || value === null || value === undefined) return false;
  if (typeof value === "number") return value !== 0 && Number.isFinite(value);
  if (typeof value === "string") return value.trim() !== "" && value !== "0" && value !== "false";
  return true;
}

function numberOperand(value: unknown, operator: string): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  throw new RoqeetExpressionError(
    "TYPE_MISMATCH",
    `Operator ${operator} requires numeric operands. Use & for string concatenation.`,
  );
}

function rollRoqeetDice(notation: string, random: () => number): number {
  const match = /^(\d*)d(\d+)([+-]\d+)?$/i.exec(notation.trim());
  if (!match) return 0;
  const count = match[1] ? Number.parseInt(match[1], 10) : 1;
  const sides = Number.parseInt(match[2] ?? "", 10);
  const modifier = match[3] ? Number.parseInt(match[3], 10) : 0;
  if (!Number.isFinite(count) || !Number.isFinite(sides) || count < 1 || count > 100 || sides < 1) {
    return 0;
  }
  let total = modifier;
  for (let i = 0; i < count; i++) {
    total += 1 + Math.floor(random() * sides);
  }
  return total;
}

function toNumber(value: unknown): number {
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : 0;
}
